/**
 * IndustrialIQ Professional Theme Configuration
 * Enterprise-grade design system for industrial operations platform
 */

export const theme = {
  token: {
    // Primary Brand Colors - Professional Blue Palette
    colorPrimary: '#0B3D91',       // Deep space blue (NASA-inspired for industrial trust)
    colorPrimaryHover: '#0952B8',  // Slightly lighter on hover
    colorPrimaryActive: '#082F70', // Darker on click
    
    // Success Colors
    colorSuccess: '#16A34A',       // Forest green for positive actions
    colorSuccessHover: '#22C55E',
    
    // Warning Colors
    colorWarning: '#D97706',       // Amber for caution
    colorWarningHover: '#F59E0B',
    
    // Error Colors
    colorError: '#DC2626',         // Red for critical issues
    colorErrorHover: '#EF4444',
    
    // Neutral Grays - Professional and Clean
    colorBgBase: '#FFFFFF',        // Pure white background
    colorBgContainer: '#FAFAFA',   // Light gray containers
    colorBgLayout: '#F5F5F5',      // Page background
    colorBorder: '#E5E7EB',        // Subtle borders
    colorBorderSecondary: '#D1D5DB',
    
    colorText: '#111827',          // Near black text
    colorTextSecondary: '#6B7280', // Gray secondary text
    colorTextTertiary: '#9CA3AF',  // Lighter tertiary text
    colorTextQuaternary: '#D1D5DB', // Very subtle text
    
    // Typography Scale - Optimized for readability
    fontSize: 16,                  // Base font size for accessibility
    fontSizeHeading1: 32,          // H1 - Page titles
    fontSizeHeading2: 24,          // H2 - Section headers
    fontSizeHeading3: 20,          // H3 - Subsections
    fontSizeHeading4: 18,          // H4 - Card titles
    fontSizeHeading5: 16,          // H5 - Small headers
    fontSizeLG: 18,                // Large text
    fontSizeSM: 14,                // Small text
    fontSizeXS: 12,                // Extra small text
    
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeightStrong: 600,         // Semibold for emphasis
    
    // Line Heights - Improved readability
    lineHeight: 1.6,
    lineHeightHeading1: 1.2,
    lineHeightHeading2: 1.3,
    lineHeightHeading3: 1.4,
    
    // Spacing Scale - 8px grid system
    marginXS: 8,
    marginSM: 12,
    margin: 16,
    marginMD: 20,
    marginLG: 24,
    marginXL: 32,
    marginXXL: 48,
    
    paddingXS: 8,
    paddingSM: 12,
    padding: 16,
    paddingMD: 20,
    paddingLG: 24,
    paddingXL: 32,
    paddingXXL: 48,
    
    // Border Radius - Subtle rounded corners
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,
    borderRadiusXS: 4,
    
    // Shadows - Subtle elevation
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    boxShadowSecondary: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    
    // Component Sizing
    controlHeight: 40,             // Standard input/button height
    controlHeightLG: 48,           // Large controls
    controlHeightSM: 32,           // Small controls
    
    // Z-index layers
    zIndexBase: 0,
    zIndexPopupBase: 1000,
  },
  
  components: {
    // Layout customization
    Layout: {
      headerBg: '#FFFFFF',
      headerHeight: 64,
      headerPadding: '0 24px',
      siderBg: '#FAFAFA',
      bodyBg: '#F5F5F5',
    },
    
    // Card customization
    Card: {
      borderRadiusLG: 12,
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      padding: 24,
      paddingLG: 32,
    },
    
    // Button customization
    Button: {
      controlHeight: 40,
      controlHeightLG: 48,
      fontWeight: 600,
      borderRadius: 8,
      primaryShadow: 'none',
    },
    
    // Table customization
    Table: {
      headerBg: '#FAFAFA',
      headerColor: '#111827',
      headerSplitColor: '#E5E7EB',
      rowHoverBg: '#F9FAFB',
      borderColor: '#E5E7EB',
      fontSize: 15,
      cellPaddingBlock: 16,
      cellPaddingInline: 16,
    },
    
    // Input customization
    Input: {
      controlHeight: 40,
      fontSize: 15,
      borderRadius: 8,
      paddingBlock: 8,
      paddingInline: 12,
    },
    
    // Statistic customization
    Statistic: {
      titleFontSize: 14,
      contentFontSize: 32,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    
    // Progress customization
    Progress: {
      defaultColor: '#0B3D91',
      remainingColor: '#E5E7EB',
    },
    
    // Badge customization
    Badge: {
      statusSize: 8,
      dotSize: 8,
    },
    
    // Tag customization
    Tag: {
      borderRadiusSM: 6,
      fontSize: 13,
      lineHeight: 1.5,
    },
  },
};

// Semantic color utilities for application use
export const colors = {
  // Status colors with accessibility in mind
  status: {
    success: '#16A34A',
    warning: '#D97706',
    error: '#DC2626',
    info: '#0B3D91',
    processing: '#0284C7',
  },
  
  // Confidence score colors
  confidence: {
    high: '#16A34A',      // Green: >70%
    medium: '#D97706',    // Amber: 40-70%
    low: '#DC2626',       // Red: <40%
  },
  
  // Chart colors - distinct and accessible
  charts: {
    primary: '#0B3D91',
    secondary: '#0284C7',
    tertiary: '#7C3AED',
    quaternary: '#D97706',
    quinary: '#16A34A',
    senary: '#DC2626',
  },
};

// Responsive breakpoints (matching Ant Design)
export const breakpoints = {
  xs: 480,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
};

// Default export for ConfigProvider
export default theme;
